import { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import adminData from '../../mocks/adminUserManagement.json';
import { UserManagementHeader } from '../../components/admin/UserManagementHeader';
import {
  InvitePatientDialog,
  InviteProviderDialog,
  AssignCareDialog,
} from '../../components/admin/UserManagementDialogs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

export const UserManagementPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [openInvitePatient, setOpenInvitePatient] = useState(false);
  const [openInviteProvider, setOpenInviteProvider] = useState(false);
  const [openAssignCare, setOpenAssignCare] = useState(false);

  // Invite Patient Form State
  const [patientInvite, setPatientInvite] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Invite Provider Form State
  const [providerInvite, setProviderInvite] = useState({
    firstName: '',
    lastName: '',
    email: '',
    credentials: '',
    specialty: '',
  });

  // Care Team Assignment State
  const [careAssignment, setCareAssignment] = useState({
    patientId: '',
    providerId: '',
    role: 'PRIMARY_NUTRITIONIST',
  });

  const handleInvitePatient = () => {
    console.log('Inviting patient:', patientInvite);
    setOpenInvitePatient(false);
    setPatientInvite({ firstName: '', lastName: '', email: '', phone: '' });
  };

  const handleInviteProvider = () => {
    console.log('Inviting provider:', providerInvite);
    setOpenInviteProvider(false);
    setProviderInvite({ firstName: '', lastName: '', email: '', credentials: '', specialty: '' });
  };

  const handleAssignCare = () => {
    console.log('Assigning care team:', careAssignment);
    setOpenAssignCare(false);
    setCareAssignment({ patientId: '', providerId: '', role: 'PRIMARY_NUTRITIONIST' });
  };

  const handleResendInvitation = (invitationId: string) => {
    console.log('Resending invitation:', invitationId);
  };

  const handleRemoveUser = (userId: string, userType: string) => {
    console.log('Removing user:', userId, userType);
  };

  const handleUnassignProvider = (patientId: string, providerId: string) => {
    console.log('Unassigning provider:', providerId, 'from patient:', patientId);
  };

  return (
    <Box>
      <UserManagementHeader
        organizationName={adminData.organization.name}
        patientCount={adminData.organization.patientCount}
        providerCount={adminData.organization.providerCount}
        pendingInvitesCount={adminData.pendingInvitations.filter((inv) => inv.status === 'PENDING').length}
      />

      {/* Tabs */}
      <Paper>

      <InvitePatientDialog
        open={openInvitePatient}
        patientInvite={patientInvite}
        setPatientInvite={setPatientInvite}
        onClose={() => setOpenInvitePatient(false)}
        onSubmit={handleInvitePatient}
      />
      <InviteProviderDialog
        open={openInviteProvider}
        providerInvite={providerInvite}
        setProviderInvite={setProviderInvite}
        onClose={() => setOpenInviteProvider(false)}
        onSubmit={handleInviteProvider}
      />
      <AssignCareDialog
        open={openAssignCare}
        careAssignment={careAssignment}
        setCareAssignment={setCareAssignment}
        patients={adminData.patients}
        providers={adminData.providers}
        onClose={() => setOpenAssignCare(false)}
        onSubmit={handleAssignCare}
      />
        <Tabs
          value={tabValue}
          onChange={(_e, newValue) => setTabValue(newValue)}
          aria-label="user management tabs"
        >
          <Tab label="All Users" />
          <Tab label="Invitations" />
          <Tab label="Care Team Assignments" />
        </Tabs>

        {/* Tab 1: All Users */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenInvitePatient(true)}
              >
                Invite Patient
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenInviteProvider(true)}
              >
                Invite Provider
              </Button>
            </Stack>

            {/* Patients Table */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Patients
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined Date</TableCell>
                    <TableCell>Assigned Providers</TableCell>
                    <TableCell>Last Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminData.patients.map((patient) => (
                    <TableRow key={patient.id}>
                      <TableCell>
                        {patient.firstName} {patient.lastName}
                      </TableCell>
                      <TableCell>{patient.email}</TableCell>
                      <TableCell>{patient.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={patient.status}
                          color={patient.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{patient.joinedDate}</TableCell>
                      <TableCell>{patient.assignedProviders.length}</TableCell>
                      <TableCell>{patient.lastActive}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Assign Care Team">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setCareAssignment({ ...careAssignment, patientId: patient.id });
                              setOpenAssignCare(true);
                            }}
                          >
                            <AssignmentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUser(patient.id, 'patient')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Providers Table */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Care Providers
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Credentials</TableCell>
                    <TableCell>Specialty</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Patient Load</TableCell>
                    <TableCell>Last Active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminData.providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        {provider.firstName} {provider.lastName}
                      </TableCell>
                      <TableCell>{provider.credentials}</TableCell>
                      <TableCell>{provider.specialty}</TableCell>
                      <TableCell>{provider.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={provider.status}
                          color={provider.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {provider.assignedPatients} / {provider.maxCapacity}
                      </TableCell>
                      <TableCell>{provider.lastActive}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUser(provider.id, 'provider')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 2: Invitations */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenInvitePatient(true)}
              >
                Invite Patient
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenInviteProvider(true)}
              >
                Invite Provider
              </Button>
            </Stack>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell>Invited Date</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminData.pendingInvitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>
                        {invitation.firstName} {invitation.lastName}
                      </TableCell>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.role}
                          color={invitation.role === 'PATIENT' ? 'primary' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{invitation.invitedBy}</TableCell>
                      <TableCell>{invitation.invitedDate}</TableCell>
                      <TableCell>{invitation.expiresDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.status}
                          color={
                            invitation.status === 'PENDING'
                              ? 'warning'
                              : invitation.status === 'EXPIRED'
                              ? 'error'
                              : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Resend Invitation">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResendInvitation(invitation.id)}
                            disabled={invitation.status === 'EXPIRED'}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel Invitation">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUser(invitation.id, 'invitation')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 3: Care Team Assignments */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Button
              variant="contained"
              startIcon={<AssignmentIcon />}
              onClick={() => setOpenAssignCare(true)}
              sx={{ mb: 3 }}
            >
              New Assignment
            </Button>

            {adminData.careTeamAssignments.map((assignment) => (
              <Paper key={assignment.patientId} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {assignment.patientName}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Assigned Date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignment.assignments.map((assign) => (
                        <TableRow key={assign.providerId}>
                          <TableCell>{assign.providerName}</TableCell>
                          <TableCell>
                            <Chip
                              label={assign.role.replace(/_/g, ' ')}
                              color={assign.role === 'PRIMARY_NUTRITIONIST' ? 'primary' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{assign.assignedDate}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove Assignment">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleUnassignProvider(assignment.patientId, assign.providerId)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        </TabPanel>
      </Paper>

      <InvitePatientDialog
        open={openInvitePatient}
        patientInvite={patientInvite}
        setPatientInvite={setPatientInvite}
        onClose={() => setOpenInvitePatient(false)}
        onSubmit={handleInvitePatient}
      />
      <InviteProviderDialog
        open={openInviteProvider}
        providerInvite={providerInvite}
        setProviderInvite={setProviderInvite}
        onClose={() => setOpenInviteProvider(false)}
        onSubmit={handleInviteProvider}
      />
      <AssignCareDialog
        open={openAssignCare}
        careAssignment={careAssignment}
        setCareAssignment={setCareAssignment}
        patients={adminData.patients}
        providers={adminData.providers}
        onClose={() => setOpenAssignCare(false)}
        onSubmit={handleAssignCare}
      />
    </Box>
  );
};

